import { getStore } from '@/globals';
import LocalCache from '@/utils/LocalCache';

import Exchange, { IExchangeAPI } from './Exchange';
import OrganizationFeatures, { IOrganizationFeaturesAPI } from './OrganizationFeatures';
import OrganizationType from './OrganizationType.enum';

export interface IOrganizationAPI {
  organization_id: number;
  name: string;
  type: OrganizationType;
  exchange_id: number;
  created_on_utc: string;
  created_by: number;
  modified_on_utc: string;
  modified_by: number;
  logo_url: string;
  currency: string;
  currency_symbol: string;
  active: boolean;
  organization_features: IOrganizationFeaturesAPI;
  exchange: IExchangeAPI;
  countries: string[];
  timezone: string;
  domain: string;
  ui_domain: string;
  is_sidekick: boolean;
  ssp_id: number;
  parent_organization_id: number;
  child_organizations: IOrganizationAPI[];
}

class Organization {
  id: number;
  name: string;
  type: OrganizationType;
  exchangeID: number;
  createdOnUtc: string;
  createdBy: number;
  modifiedOnUtc: string;
  modifiedBy: number;
  logoUrl: string;
  currency: string;
  currencySymbol: string;
  isActive: boolean;
  organizationFeatures: OrganizationFeatures | null;
  exchange: Exchange | null;
  countries: string[];
  timezone: string;
  domain: string;
  uiDomain: string;
  isSidekick: boolean;
  sspID: number;
  parentOrganizationID: number;
  childOrganizations: Organization[] = [];

  isTTD: boolean;

  constructor(
    id: number,
    name: string,
    type: OrganizationType,
    exchangeID: number,
    createdOnUtc: string,
    createdBy: number,
    modifiedOnUtc: string,
    modifiedBy: number,
    logoUrl: string,
    currency: string,
    currencySymbol: string,
    isActive: boolean,
    organizationFeatures: OrganizationFeatures | null,
    exchange: Exchange | null,
    countries: string[],
    timezone: string,
    domain: string,
    uiDomain: string,
    isSidekick: boolean,
    sspID: number,
    parentOrganizationID: number,
    childOrganizations: Organization[] = [],
  ) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.exchangeID = exchangeID;
    this.createdOnUtc = createdOnUtc;
    this.createdBy = createdBy;
    this.modifiedOnUtc = modifiedOnUtc;
    this.modifiedBy = modifiedBy;
    this.logoUrl = logoUrl;
    this.currency = currency;
    this.currencySymbol = currencySymbol;
    this.isActive = isActive;
    this.organizationFeatures = organizationFeatures;
    this.exchange = exchange;
    this.countries = countries;
    this.timezone = timezone;
    this.domain = domain;
    this.uiDomain = uiDomain;
    this.isSidekick = isSidekick;
    this.sspID = sspID;
    this.parentOrganizationID = parentOrganizationID;
    this.childOrganizations = childOrganizations;
    this.isTTD = false;
  }

  async getIsTTD() {
    // We put this in a separate method so that it can be dispatched only once when we need it.
    // And we store it globally for the organization.
    // It needs to be in an independant method because it needs to be `await`ed.
    if (this.isSidekick) {
      return await getStore().dispatch('orgApp/orgIsLinkedToDsp', { dspCode: 'TTD' });
    }
    return false;
  }

  static fromApi(apiObject: IOrganizationAPI): Organization {
    const isSampleDataOn = LocalCache.get('isSampleDataOn') === 'true';

    return new Organization(
      apiObject.organization_id,
      isSampleDataOn && !apiObject.name.startsWith('Sample') ? `Sample ${apiObject.name}` : apiObject.name,
      apiObject.type,
      apiObject.exchange_id,
      apiObject.created_on_utc,
      apiObject.created_by,
      apiObject.modified_on_utc,
      apiObject.modified_by,
      apiObject.logo_url,
      apiObject.currency,
      apiObject.currency_symbol,
      apiObject.active,
      apiObject.organization_features ? OrganizationFeatures.fromAPI(apiObject.organization_features) : null,
      apiObject.exchange ? Exchange.fromApi(apiObject.exchange) : null,
      apiObject.countries,
      apiObject.timezone,
      apiObject.domain,
      apiObject.ui_domain,
      apiObject.is_sidekick,
      apiObject.ssp_id,
      apiObject.parent_organization_id,
    );
  }

  get isDemandParentOrganization(): boolean {
    return this.type === OrganizationType.DEMAND_PARENT;
  }

  get isSupplyParentOrganization(): boolean {
    return this.type === OrganizationType.SUPPLY_PARENT;
  }

  get isDemandOrganization(): boolean {
    return this.type === OrganizationType.DEMAND || this.isDemandParentOrganization;
  }

  get isSupplyOrganization(): boolean {
    return this.type === OrganizationType.SUPPLY || this.isSupplyParentOrganization;
  }

  get isParentOrganization(): boolean {
    return this.isDemandParentOrganization || this.isSupplyParentOrganization;
  }

  get isCuratorOrganization(): boolean {
    return this.type === OrganizationType.CURATOR;
  }

  get isStroeerOrganization(): boolean {
    return this.domain?.toLowerCase().includes('stroeer') || this.uiDomain?.toLowerCase().includes('stroeer');
  }

  get isSidekickOrganization(): boolean {
    return this.isSidekick;
  }

  get isTTDOrganization(): boolean {
    return this.isTTD;
  }

  get isExchange(): boolean {
    return false;
  }

  get className(): string {
    let className: string;
    switch (this.type) {
      case OrganizationType.DEMAND:
        className = 'dsp-org';
        break;
      case OrganizationType.SUPPLY:
        className = 'ssp-org';
        break;
      case OrganizationType.DEMAND_PARENT:
        className = 'parent-dsp-org';
        break;
      case OrganizationType.SUPPLY_PARENT:
        className = 'parent-ssp-org';
        break;
      default:
        className = 'curator-org';
        break;
    }
    return className;
  }

  get idAndName(): string {
    return `${this.id} - ${this.name}`;
  }

  toApi(): IOrganizationAPI {
    return {
      organization_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      type: this.type,
      exchange_id: this.exchangeID,
      created_on_utc: this.createdOnUtc,
      created_by: this.createdBy,
      modified_on_utc: this.modifiedOnUtc,
      modified_by: this.modifiedBy,
      logo_url: this.logoUrl,
      currency: this.currency,
      currency_symbol: this.currencySymbol,
      active: this.isActive,
      organization_features: this.organizationFeatures ? this.organizationFeatures.toAPI() : null,
      exchange: this.exchange ? this.exchange.toApi() : null,
      countries: this.countries,
      timezone: this.timezone,
      domain: this.domain,
      ui_domain: this.uiDomain,
      is_sidekick: this.isSidekick,
      ssp_id: this.sspID,
      parent_organization_id: this.parentOrganizationID,
      child_organizations: this.childOrganizations.map((childOrg) => childOrg.toApi()),
    };
  }

  static fromObject(obj: any): Organization {
    return new Organization(
      obj.id,
      obj.name,
      obj.type,
      obj.exchangeID,
      obj.createdOnUtc,
      obj.createdBy,
      obj.modifiedOnUtc,
      obj.modifiedBy,
      obj.logoUrl,
      obj.currency,
      obj.currencySymbol,
      obj.isActive,
      new OrganizationFeatures(
        obj.organizationID,
        obj.organizationFeatures.inventoryManagement,
        obj.organizationFeatures.ssp,
        obj.organizationFeatures.adServer,
        obj.organizationFeatures.dspCampaigns,
        obj.organizationFeatures.dspDeals,
        obj.organizationFeatures.reporting,
        obj.organizationFeatures.customAudiences,
        obj.organizationFeatures.dashboard,
        obj.organizationFeatures.approvals,
        obj.organizationFeatures.organizationManagement,
        obj.organizationFeatures.creatives,
      ),
      new Exchange(
        obj.exchange.id,
        obj.exchange.name,
        obj.exchange.createdOnUtc,
        obj.exchange.modifiedOnUtc,
        obj.exchange.isWhiteLabeled,
      ),
      obj.countries,
      obj.timezone,
      obj.domain,
      obj.uiDomain,
      obj.isSidekick,
      obj.sspID,
      obj.parentOrganizationID,
      obj.childOrganizations?.map((childOrg) => Organization.fromObject(childOrg)),
    );
  }
}

export default Organization;
